/* ============================================================================
   Him.clo premium animations — pure vanilla, IntersectionObserver + RAF
   Loaded globally via layout/theme.liquid. No dependencies.
   ============================================================================ */
(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  function init() {
    scrollReveal();
    parallaxHero();
    textReveal();
    pageTransition();
    counterAnimation();
    toastSystem();
    cursorSpotlight();
    marqueeHoverPause();
    recentlyViewed();
    predictiveSearch();
    freeShippingBar();
    backToTop();
    wishlistPersist();
    progressiveImages();
  }

  /* ---------------------------------------------------------------------
     10. Predictive search — instant product suggestions as user types
     --------------------------------------------------------------------- */
  function predictiveSearch() {
    const input = document.querySelector('[data-himclo-search-input]');
    if (!input) return;

    const form = input.closest('form');
    if (!form) return;

    // Insert dropdown right below the form
    const dropdown = document.createElement('div');
    dropdown.className = 'hc-predict';
    dropdown.setAttribute('aria-live', 'polite');
    dropdown.hidden = true;
    form.parentNode.insertBefore(dropdown, form.nextSibling);

    let debounce = 0;
    let currentAbort = null;
    let lastQuery = '';

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(debounce);
      if (q === lastQuery) return;
      if (!q || q.length < 2) { dropdown.hidden = true; dropdown.innerHTML = ''; return; }
      debounce = setTimeout(() => runSearch(q), 220);
    });

    input.addEventListener('focus', () => {
      if (dropdown.innerHTML) dropdown.hidden = false;
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.hc-predict') && !e.target.closest('form') && e.target !== input) {
        dropdown.hidden = true;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { dropdown.hidden = true; }
    });

    function runSearch(q) {
      lastQuery = q;
      if (currentAbort) currentAbort.abort();
      currentAbort = new AbortController();

      const url = '/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title,vendor';

      fetch(url, { headers: { Accept: 'application/json' }, signal: currentAbort.signal })
        .then((r) => r.json())
        .then((data) => {
          const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
          if (!products.length) {
            dropdown.innerHTML = `<div class="hc-predict__empty">No products match "${escapeHTML(q)}"</div>`;
            dropdown.hidden = false;
            return;
          }
          dropdown.innerHTML = `
            <div class="hc-predict__list">
              ${products.map((p) => `
                <a href="${p.url}" class="hc-predict__item">
                  <div class="hc-predict__thumb">
                    ${p.image ? `<img src="${p.image}" alt="" loading="lazy">` : ''}
                  </div>
                  <div class="hc-predict__meta">
                    <div class="hc-predict__title">${escapeHTML(p.title)}</div>
                    <div class="hc-predict__price">${p.price || ''}</div>
                  </div>
                </a>
              `).join('')}
            </div>
            <a href="/search?q=${encodeURIComponent(q)}" class="hc-predict__viewall">View all results for "${escapeHTML(q)}"</a>
          `;
          dropdown.hidden = false;
        })
        .catch((err) => {
          if (err.name !== 'AbortError') dropdown.hidden = true;
        });
    }
  }

  /* ---------------------------------------------------------------------
     11. Free-shipping progress bar in cart drawer
     --------------------------------------------------------------------- */
  function freeShippingBar() {
    const threshold = 150; // $ — configurable
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const render = (subtotalCents) => {
      const remaining = Math.max(0, threshold - (subtotalCents / 100));
      const progress = Math.min(1, (subtotalCents / 100) / threshold);
      let bar = drawer.querySelector('.hc-ship-bar');
      if (!bar) {
        bar = document.createElement('div');
        bar.className = 'hc-ship-bar';
        const header = drawer.querySelector('.drawer__header, .drawer__heading');
        if (header && header.parentNode) header.parentNode.insertBefore(bar, header.nextSibling);
        else drawer.insertBefore(bar, drawer.firstChild);
      }
      const isFree = remaining <= 0;
      bar.innerHTML = `
        <div class="hc-ship-bar__msg">
          ${isFree
            ? `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg> You qualify for <strong>free shipping</strong>!`
            : `Add <strong>$${remaining.toFixed(2)}</strong> more for <strong>free shipping</strong>`}
        </div>
        <div class="hc-ship-bar__track"><div class="hc-ship-bar__fill" style="width:${(progress * 100).toFixed(1)}%"></div></div>
      `;
      bar.classList.toggle('hc-ship-bar--complete', isFree);
    };

    const refresh = () => {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => render(cart.total_price || 0))
        .catch(() => {});
    };

    refresh();
    document.addEventListener('himclo:cart:updated', refresh);
  }

  /* ---------------------------------------------------------------------
     12. Back to top button
     --------------------------------------------------------------------- */
  function backToTop() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hc-back-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>';
    document.body.appendChild(btn);

    let scheduled = false;
    const update = () => {
      btn.classList.toggle('hc-back-top--visible', window.scrollY > 500);
      scheduled = false;
    };
    window.addEventListener('scroll', () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
    });
  }

  /* ---------------------------------------------------------------------
     13. Wishlist persistence (localStorage-backed hearts)
     --------------------------------------------------------------------- */
  function wishlistPersist() {
    const KEY = 'himclo_wishlist';
    const read = () => {
      try { return new Set(JSON.parse(localStorage.getItem(KEY) || '[]')); }
      catch (e) { return new Set(); }
    };
    const write = (set) => {
      try { localStorage.setItem(KEY, JSON.stringify([...set])); } catch (e) {}
      updateHeaderCount(set.size);
    };
    const state = read();

    // Header count badge
    const updateHeaderCount = (n) => {
      const account = document.querySelector('.himclo-header a[href*="/account"]');
      if (!account) return;
      let badge = account.querySelector('.hc-wish-badge');
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'hc-wish-badge';
          account.appendChild(badge);
          account.style.position = 'relative';
        }
        badge.textContent = String(n);
      } else if (badge) {
        badge.remove();
      }
    };
    updateHeaderCount(state.size);

    const productHandleFromCard = (card) => {
      const link = card.querySelector('a[href*="/products/"]');
      if (!link) return null;
      const path = link.getAttribute('href').split('?')[0].split('#')[0];
      const m = path.match(/\/products\/([^/]+)/);
      return m ? m[1] : null;
    };

    const applyState = () => {
      document.querySelectorAll('.himclo-pd-heart').forEach((heart) => {
        const card = heart.closest('.card-wrapper, .card, .product-card-wrapper');
        if (!card) return;
        const handle = productHandleFromCard(card);
        if (!handle) return;
        if (state.has(handle)) heart.classList.add('himclo-pd-heart--active');
        heart.dataset.productHandle = handle;
      });
    };

    // Intercept clicks on any heart button
    document.addEventListener('click', (e) => {
      const heart = e.target.closest('.himclo-pd-heart');
      if (!heart) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = heart.dataset.productHandle;
      if (!handle) return;
      const isActive = state.has(handle);
      if (isActive) {
        state.delete(handle);
        heart.classList.remove('himclo-pd-heart--active');
        if (window.himclo && window.himclo.toast) window.himclo.toast('Removed from wishlist');
      } else {
        state.add(handle);
        heart.classList.add('himclo-pd-heart--active');
        if (window.himclo && window.himclo.toast) window.himclo.toast('Added to wishlist', { variant: 'success' });
      }
      write(state);
    }, true);

    applyState();
    // Re-apply when new cards get added to DOM (related products, prefetched pages)
    new MutationObserver(applyState).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------------
     14. Progressive image fade-in on load
     --------------------------------------------------------------------- */
  function progressiveImages() {
    if (REDUCED_MOTION) return;

    const targets = document.querySelectorAll(
      '.hp-card__img, .himclo-cg__image, .himclo-pc__image, .himclo-tg__image, ' +
      '.himclo-lb__image, .himclo-dual__image, .related-products .card__media img, ' +
      '.hc-recent__media img, .card__media img'
    );

    targets.forEach((img) => {
      if (img.__hcProg) return;
      img.__hcProg = true;
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('hc-img-loaded');
      } else {
        img.classList.add('hc-img-loading');
        img.addEventListener('load', () => {
          img.classList.remove('hc-img-loading');
          img.classList.add('hc-img-loaded');
        }, { once: true });
        img.addEventListener('error', () => {
          img.classList.remove('hc-img-loading');
        }, { once: true });
      }
    });

    // Re-scan for dynamically added images (recently viewed, prefetch, AJAX)
    new MutationObserver(() => progressiveImages()).observe(document.body, {
      childList: true, subtree: true,
    });
  }


  /* ---------------------------------------------------------------------
     7. Cursor-following spotlight on the dark hero (desktop only)
     --------------------------------------------------------------------- */
  function cursorSpotlight() {
    if (REDUCED_MOTION) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.innerWidth < 990) return;

    const hero = document.querySelector('.hero-himclo');
    if (!hero) return;

    // Only on dark-theme pages (homepage). Skip if forced light.
    if (document.documentElement.getAttribute('data-template-lock') === 'light') return;

    const spotlight = document.createElement('div');
    spotlight.className = 'hc-spotlight';
    spotlight.setAttribute('aria-hidden', 'true');
    hero.appendChild(spotlight);

    let raf = 0;
    let x = 50, y = 50;
    let tx = 50, ty = 50;

    hero.addEventListener('mousemove', (e) => {
      const rect = hero.getBoundingClientRect();
      tx = ((e.clientX - rect.left) / rect.width) * 100;
      ty = ((e.clientY - rect.top) / rect.height) * 100;
      if (!raf) raf = requestAnimationFrame(loop);
    });
    hero.addEventListener('mouseleave', () => {
      tx = 50; ty = 50;
      if (!raf) raf = requestAnimationFrame(loop);
    });

    function loop() {
      // Ease toward target
      x += (tx - x) * 0.1;
      y += (ty - y) * 0.1;
      spotlight.style.setProperty('--sx', x + '%');
      spotlight.style.setProperty('--sy', y + '%');
      if (Math.abs(tx - x) > 0.3 || Math.abs(ty - y) > 0.3) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = 0;
      }
    }
  }

  /* ---------------------------------------------------------------------
     8. Marquee announcement bar — pause on hover
     --------------------------------------------------------------------- */
  function marqueeHoverPause() {
    const tracks = document.querySelectorAll('.himclo-ab__track');
    tracks.forEach((t) => {
      const parent = t.closest('.himclo-ab');
      if (!parent) return;
      parent.addEventListener('mouseenter', () => { t.style.animationPlayState = 'paused'; });
      parent.addEventListener('mouseleave', () => { t.style.animationPlayState = 'running'; });
    });
  }

  /* ---------------------------------------------------------------------
     9. Recently viewed products — localStorage-backed
         Records on product pages; renders a section on homepage + product page.
     --------------------------------------------------------------------- */
  function recentlyViewed() {
    const KEY = 'himclo_recent';
    const MAX = 8;

    function read() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
      catch (e) { return []; }
    }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
    }

    // Record on product pages
    if (document.body.classList.contains('template-product')) {
      const titleEl = document.querySelector('.product__title h1, .product__title h2, .product__title');
      const priceEl = document.querySelector('.product__price .price-item, .price-item--last, .price-item--regular');
      const imgEl   = document.querySelector('.product__media img, .product__media-item img');
      const path    = window.location.pathname.split('?')[0];
      if (titleEl && path.indexOf('/products/') !== -1) {
        const entry = {
          url: path,
          title: (titleEl.textContent || '').trim(),
          price: priceEl ? (priceEl.textContent || '').trim() : '',
          img: imgEl ? (imgEl.currentSrc || imgEl.src) : '',
          ts: Date.now(),
        };
        const list = read().filter((x) => x.url !== path);
        list.unshift(entry);
        write(list);
      }
    }

    // Render on homepage + product page (bottom)
    const shouldRender =
      document.body.classList.contains('template-index') ||
      document.body.classList.contains('template-product');
    if (!shouldRender) return;

    const list = read().filter((x) => x.url !== window.location.pathname);
    if (list.length < 2) return; // not enough to bother

    const section = document.createElement('section');
    section.className = 'hc-recent himclo-section';
    section.setAttribute('aria-label', 'Recently viewed');
    const items = list.slice(0, 6);
    section.innerHTML = `
      <div class="himclo-wrap">
        <div class="himclo-section-head">
          <h2 class="himclo-section-title">Recently viewed</h2>
        </div>
        <div class="hc-recent__grid" style="--cols:${items.length}">
          ${items.map((x) => `
            <a href="${x.url}" class="hc-recent__card">
              <div class="hc-recent__media">
                ${x.img ? `<img src="${x.img}" alt="${escapeHTML(x.title)}" loading="lazy">` : ''}
              </div>
              <div class="hc-recent__body">
                <div class="hc-recent__title">${escapeHTML(x.title)}</div>
                ${x.price ? `<div class="hc-recent__price">${escapeHTML(x.price)}</div>` : ''}
              </div>
            </a>
          `).join('')}
        </div>
      </div>
    `;

    // Insert before footer (or at end of main)
    const footer = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
    if (footer && footer.parentNode) {
      footer.parentNode.insertBefore(section, footer);
    } else {
      document.body.appendChild(section);
    }
  }


  /* ---------------------------------------------------------------------
     1. Scroll-triggered section reveal (below-fold)
     --------------------------------------------------------------------- */
  function scrollReveal() {
    if (REDUCED_MOTION || !('IntersectionObserver' in window)) return;

    const targets = document.querySelectorAll(
      '.himclo-section, .collection-hero, .related-products, .himclo-story, ' +
      '.himclo-dual, .himclo-tg, .himclo-lb, .himclo-tm, .himclo-cg, ' +
      '.himclo-icon-row, .himclo-cp, .himclo-footer, .article-template, ' +
      '.template-page .rte, .template-page-contact .contact-form, ' +
      '.himclo-pd-tabs, .himclo-pd-trust'
    );

    targets.forEach((el) => {
      el.classList.add('hc-reveal-init');
    });

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('hc-reveal-in');
          io.unobserve(e.target);
        }
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.05,
    });

    targets.forEach((el) => io.observe(el));
  }

  /* ---------------------------------------------------------------------
     2. Parallax hero image (subtle scroll-linked translate)
     --------------------------------------------------------------------- */
  function parallaxHero() {
    if (REDUCED_MOTION) return;

    const heroImages = document.querySelectorAll(
      '.hero-himclo__media, .collection-hero__image-container, .himclo-story__media'
    );
    if (!heroImages.length) return;

    let scheduled = false;

    const update = () => {
      const winHeight = window.innerHeight;
      heroImages.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // Only parallax while in viewport
        if (rect.bottom < 0 || rect.top > winHeight) return;
        // -12% to +12% translate based on scroll progress
        const progress = (rect.top - winHeight) / (winHeight + rect.height);
        const clamped = Math.max(-1, Math.min(1, progress));
        const y = clamped * 12; // 12% max drift
        const img = el.querySelector('img');
        if (img) {
          img.style.transform = `translate3d(0, ${y}%, 0)`;
          img.style.willChange = 'transform';
        }
      });
      scheduled = false;
    };

    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ---------------------------------------------------------------------
     3. Text reveal on scroll — word-by-word for section headings
     --------------------------------------------------------------------- */
  function textReveal() {
    if (REDUCED_MOTION) return;

    const headings = document.querySelectorAll(
      '.himclo-section-title, .himclo-story__heading, .himclo-dual__title, ' +
      '.himclo-cp__heading, .collection-hero__title, .hero-himclo__heading'
    );

    headings.forEach((h) => {
      if (h.dataset.hcSplit) return;
      h.dataset.hcSplit = '1';
      const text = h.textContent.trim();
      if (!text || text.length > 120) return; // skip long text

      const words = text.split(/\s+/);
      h.innerHTML = words.map((w, i) =>
        `<span class="hc-word" style="--i:${i}"><span class="hc-word-inner">${escapeHTML(w)}</span></span>`
      ).join(' ');
    });

    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('hc-text-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.2 });

    headings.forEach((h) => io.observe(h));
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------------------------------------------------------------------
     4. Page transition fade — smooth exit on internal link click
     --------------------------------------------------------------------- */
  function pageTransition() {
    if (REDUCED_MOTION) return;

    // Fade in on load
    document.documentElement.classList.add('hc-page-in');

    // Fade out on internal navigation
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href) return;
      // Skip external / anchor / mailto / tel / new-tab / modifier keys
      if (href.startsWith('#')) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (link.target === '_blank') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      try {
        const url = new URL(link.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        // Skip cart / account / checkout (Shopify handles those)
        if (url.pathname.startsWith('/checkout') || url.pathname.startsWith('/cart/')) return;
      } catch (err) { return; }

      e.preventDefault();
      document.documentElement.classList.add('hc-page-out');
      setTimeout(() => { window.location.href = link.href; }, 240);
    });

    // Handle back/forward — pageshow fires when coming back from bfcache
    window.addEventListener('pageshow', () => {
      document.documentElement.classList.remove('hc-page-out');
      document.documentElement.classList.add('hc-page-in');
    });
  }

  /* ---------------------------------------------------------------------
     5. Number counter — animates from 0 to target on scroll into view
     --------------------------------------------------------------------- */
  function counterAnimation() {
    if (REDUCED_MOTION || !('IntersectionObserver' in window)) return;

    const counters = document.querySelectorAll('[data-hc-counter]');
    if (!counters.length) return;

    const animate = (el) => {
      const target = parseFloat(el.getAttribute('data-hc-counter')) || 0;
      const decimals = (String(target).split('.')[1] || '').length;
      const duration = 1600;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        const val = target * eased;
        el.textContent = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString();
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          animate(e.target);
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.5 });

    counters.forEach((c) => io.observe(c));
  }

  /* ---------------------------------------------------------------------
     6. Toast notifications — window.himclo.toast(msg, opts)
     --------------------------------------------------------------------- */
  function toastSystem() {
    if (window.himclo && window.himclo.toast) return; // already set up
    let container = document.querySelector('.hc-toast-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'hc-toast-container';
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'false');
      document.body.appendChild(container);
    }

    const show = (message, opts = {}) => {
      const t = document.createElement('div');
      t.className = 'hc-toast' + (opts.variant ? ' hc-toast--' + opts.variant : '');
      t.setAttribute('role', 'status');
      const icon = opts.variant === 'success'
        ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
        : opts.variant === 'error'
          ? '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>'
          : '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
      t.innerHTML = `<span class="hc-toast__icon">${icon}</span><span class="hc-toast__msg">${escapeHTML(message)}</span>`;
      container.appendChild(t);
      requestAnimationFrame(() => t.classList.add('hc-toast--in'));

      const timeout = opts.duration || 3500;
      setTimeout(() => {
        t.classList.remove('hc-toast--in');
        t.classList.add('hc-toast--out');
        setTimeout(() => t.remove(), 350);
      }, timeout);
    };

    window.himclo = window.himclo || {};
    window.himclo.toast = show;

    // Auto-toast on cart add
    document.addEventListener('himclo:cart:updated', (e) => {
      const n = (e.detail && typeof e.detail.count === 'number') ? e.detail.count : 0;
      if (n > 0) show('Added to cart', { variant: 'success' });
    });
  }
})();
